import { create } from 'zustand';
import { fetchWithAuth } from '@/services/api';
import { sanitizeLojaDto } from '@/services/configDto';
import { Loja, AppUserProfile } from '@/types';
import { useAuthStore } from './authStore';
import { clearLeadMessagesCache } from '@/lib/query/queryClient';
import { clearConversationsCache } from '@/lib/query/useConversations';
import { clearLeadsCache } from '@/lib/query/useLeadsQuery';
import { useLeadsStore } from './leadsStore';

interface LojaAtivaState {
  lojaAtivaId: string | null;
  selectionUserId: string | null;
  lojasAcessiveis: Loja[];
  setLojaAtiva: (id: string | null) => void;
  fetchMinhasLojas: (profile?: AppUserProfile | null) => Promise<void>;
  init: () => Promise<void>;
  getLojaAtiva: () => Loja | null;
}

export const useLojaAtivaStore = create<LojaAtivaState>((set, get) => ({
  lojaAtivaId: null,
  selectionUserId: null,
  lojasAcessiveis: [],

  setLojaAtiva: (id: string | null) => {
    if (get().lojaAtivaId !== id) {
      clearLeadMessagesCache();
      clearConversationsCache();
      clearLeadsCache();
      useLeadsStore.getState().stopRealtime?.(false); // Interrompe realtime da loja anterior (B4)
      useLeadsStore.setState({ leads: [] });
    }
    const profile = useAuthStore.getState().profile;
    const userId = profile?.auth_user_id || profile?.id;
    if (userId) {
      try { localStorage.setItem(`task-crm-store-selection:${userId}`, JSON.stringify(id)); } catch { /* storage indisponivel */ }
    }
    set({ lojaAtivaId: id, selectionUserId: userId || null });
  },

  fetchMinhasLojas: async (restoredProfile) => {
    const profile = restoredProfile ?? useAuthStore.getState().profile;
    const userId = profile?.auth_user_id || profile?.id || null;
    try {
      const res = await fetchWithAuth('/api/my-lojas');
      if (res.ok) {
        const data = await res.json();
        const lojas: Loja[] = (Array.isArray(data.lojas) ? data.lojas : []).map(sanitizeLojaDto);
        set({ lojasAcessiveis: lojas });

        // Preferencia por identidade; nunca e uma autorizacao de acesso.
        // So restaurar IDs que a API autenticada ainda retorna para este usuario.
        const currentActiveId = get().lojaAtivaId;
        let savedId: string | null = null;
        if (userId) {
          try {
            const saved = JSON.parse(localStorage.getItem(`task-crm-store-selection:${userId}`) || 'null');
            if (typeof saved === 'string' && lojas.some(l => l.id === saved)) savedId = saved;
          } catch { /* preferencia invalida/indisponivel nao bloqueia login */ }
        }
        const candidateId = get().selectionUserId === userId ? currentActiveId || savedId : savedId;
        const isValidCurrent = candidateId && lojas.some(l => l.id === candidateId);
        const isAdmin = profile?.role === 'super_admin' || profile?.role === 'tenant_admin';

        let targetId = candidateId;
        if (isValidCurrent) {
          // Manter loja ativa atual se ainda for válida
        } else if (isAdmin && lojas.length === 1) {
          // Admin com uma única loja: o header só mostra um chip não-clicável (sem dropdown).
          // Sem loja ativa aqui, páginas como Follow ficam travadas pedindo uma seleção
          // impossível (incidente 21/10: "selecione uma loja no topo" sem seletor).
          targetId = lojas[0].id;
        } else if (isAdmin) {
          // Admins (multi-loja) escolhem no dropdown
          targetId = null;
        } else if (lojas.length > 0) {
          // Não-admins: padrão é a primeira loja acessível
          targetId = lojas[0].id;
        } else {
          targetId = null;
        }

        if (currentActiveId !== targetId) {
          clearLeadMessagesCache();
          clearConversationsCache();
          clearLeadsCache();
          useLeadsStore.getState().stopRealtime?.(false); // Interrompe realtime da loja anterior (B4)
          useLeadsStore.setState({ leads: [] });
        }
        set({ lojaAtivaId: targetId, selectionUserId: userId });
      }
    } catch (err) {
      console.error('[lojaAtivaStore] fetchMinhasLojas error:', err);
    }
  },

  init: async () => {
    await get().fetchMinhasLojas();
  },

  getLojaAtiva: () => {
    const { lojaAtivaId, lojasAcessiveis } = get();
    if (!lojaAtivaId) return null;
    return lojasAcessiveis.find(l => l.id === lojaAtivaId) || null;
  }
}));
